import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { AnimatePresence, motion } from "framer-motion";

import { ClipStatusTray } from "MVVM/Components/ClipStatus";
import { InFlightClipsAtom } from "MVVM/State/clipStatus";
import {
  isTimelineComplete,
  isTimelineErrored,
} from "MVVM/Components/ClipStatus/timelineDisplay";
import { DockCard } from "./DockCard";
import { DockRoot, MoreButton, VisuallyHidden } from "./styledComponents";
import { useDockMotion } from "./useDockMotion";
import { useDockNudges } from "./useDockNudges";

// Speaks only job transitions, so polling doesn't re-announce whole cards.
const useJobAnnouncement = () => {
  const timelines = useRecoilValue(InFlightClipsAtom);
  const pending = useRef(new Set<string>());
  const [message, setMessage] = useState("");

  useEffect(() => {
    timelines.forEach((timeline) => {
      const done = isTimelineComplete(timeline);
      const failed = isTimelineErrored(timeline);
      if (!done && !failed) {
        pending.current.add(timeline.trackId);
        return;
      }
      if (!pending.current.delete(timeline.trackId)) return;
      const title = timeline.clip?.title || "Your clip";
      setMessage(done ? `${title} is ready` : `${title} failed to render`);
    });
  }, [timelines]);

  return message;
};

// Publishes --bottom-stack-height so pages can pad for the corner.
const useStackHeight = () => {
  const observer = useRef<ResizeObserver | null>(null);
  return useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node) return;
    observer.current = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty(
        "--bottom-stack-height",
        `${Math.ceil(entry.contentRect.height)}px`,
      );
    });
    observer.current.observe(node);
  }, []);
};

export const Dock = () => {
  const { nudges, hiddenCount, expanded, toggleExpanded, modals } =
    useDockNudges();
  const announcement = useJobAnnouncement();
  const stackRef = useStackHeight();
  const motionProps = useDockMotion();

  return (
    <DockRoot aria-label="Notifications" ref={stackRef}>
      <VisuallyHidden aria-live="polite">{announcement}</VisuallyHidden>
      <ClipStatusTray />
      <AnimatePresence initial={false}>
        {nudges.map((nudge) => (
          <motion.div key={nudge.id} {...motionProps}>
            <DockCard {...nudge} />
          </motion.div>
        ))}
        {hiddenCount > 0 && (
          <motion.div
            key="more"
            style={{ alignSelf: "flex-end" }}
            {...motionProps}
          >
            <MoreButton
              aria-expanded={expanded}
              onClick={toggleExpanded}
              type="button"
            >
              {expanded
                ? "Show less"
                : `${hiddenCount} more notification${
                    hiddenCount === 1 ? "" : "s"
                  }`}
            </MoreButton>
          </motion.div>
        )}
      </AnimatePresence>
      {modals}
    </DockRoot>
  );
};
